import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { Breadcrumb as ChakraBreadcrumb } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';

export interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  separator?: ReactNode;
}

/**
 * DS Breadcrumbs (Navigation card). Renders a muted crumb trail with a brand,
 * semibold final crumb and faint '/' separators — the colors/sizing live in the
 * `breadcrumb` slot recipe (src/lib/theme-recipes.ts), not here. The last item
 * always renders as the current crumb; earlier items link via react-router when
 * an `href` is supplied, otherwise render as plain (muted) text.
 */
export function Breadcrumbs({ items, separator = '/' }: BreadcrumbsProps) {
  return (
    <ChakraBreadcrumb.Root>
      <ChakraBreadcrumb.List>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <Fragment key={index}>
              <ChakraBreadcrumb.Item>
                {isLast ? (
                  <ChakraBreadcrumb.CurrentLink>{item.label}</ChakraBreadcrumb.CurrentLink>
                ) : item.href ? (
                  <ChakraBreadcrumb.Link asChild>
                    <NavLink to={item.href}>{item.label}</NavLink>
                  </ChakraBreadcrumb.Link>
                ) : (
                  <ChakraBreadcrumb.Link as='span'>{item.label}</ChakraBreadcrumb.Link>
                )}
              </ChakraBreadcrumb.Item>
              {!isLast && (
                <ChakraBreadcrumb.Separator>{separator}</ChakraBreadcrumb.Separator>
              )}
            </Fragment>
          );
        })}
      </ChakraBreadcrumb.List>
    </ChakraBreadcrumb.Root>
  );
}

export default Breadcrumbs;
